"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { HomePageLayout } from "@/components/website/homepage-layout"
import { TopNewsCard } from "@/components/website/top-news-card"
import { LatestNewsList } from "@/components/website/latest-news-list"
import { ViralNewsSidebar } from "@/components/website/viral-news-sidebar"
import { WebStoriesList } from "@/components/website/web-stories-list"
import type { Article, Category } from "@/lib/types"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

const HOME_FEED_INITIAL = 5

export default function HomePage() {
  const [topNews, setTopNews] = useState<Article[]>([])
  const [latestNews, setLatestNews] = useState<Article[]>([])
  const [importantNews, setImportantNews] = useState<Article[]>([])
  const [viralNews, setViralNews] = useState<Article[]>([])
  const [webStories, setWebStories] = useState<Article[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [latestPage, setLatestPage] = useState(1)
  const [hasMoreLatest, setHasMoreLatest] = useState(true)
  const [loadingMoreLatest, setLoadingMoreLatest] = useState(false)
  const [importantPage, setImportantPage] = useState(1)
  const [hasMoreImportant, setHasMoreImportant] = useState(true)
  const [loadingMoreImportant, setLoadingMoreImportant] = useState(false)
  const [homeFeedVisibleCount, setHomeFeedVisibleCount] = useState(HOME_FEED_INITIAL)

  const normalizeList = (data: any) => {
    const list =
      (Array.isArray(data) && data) ||
      data?.items ||
      data?.data ||
      data?.results ||
      data?.articles ||
      []
    return (Array.isArray(list) ? list : []).map((a: any) => ({
      ...a,
      id: String(a.id),
    }))
  }

  const safeJson = async (res: Response) => {
    try {
      return await res.json()
    } catch (e) {
      console.error("[RootHomePage] JSON parse failed:", { url: res.url, status: res.status }, e)
      return null
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [topRes, latestRes, viralRes, webStoriesRes, categoriesRes, settingsRes] = await Promise.all([
          fetch(`${API_BASE}/public/articles/top-news?limit=1`),
          fetch(`${API_BASE}/public/articles/latest?limit=10`),
          // Fetch a larger batch of viral news once; we'll paginate it on the client
          fetch(`${API_BASE}/public/articles/viral?limit=50`),
          fetch(`${API_BASE}/public/articles/web-stories?limit=10`),
          fetch(`${API_BASE}/public/categories`),
          fetch(`${API_BASE}/website-settings`),
        ])

        const topData = topRes.ok ? await safeJson(topRes) : []
        const latestData = latestRes.ok ? await safeJson(latestRes) : []
        const viralData = viralRes.ok ? await safeJson(viralRes) : []
        const webStoriesData = webStoriesRes.ok ? await safeJson(webStoriesRes) : []
        const categoriesData = categoriesRes.ok ? await safeJson(categoriesRes) : []
        const settingsData = settingsRes.ok ? await safeJson(settingsRes) : null

        // Update document metadata from website settings
        if (typeof document !== "undefined" && settingsData) {
          const siteTitle = settingsData.site_title || "News Portal"
          const siteDescription = settingsData.site_description || "Your trusted source for the latest news and updates"
          
          document.title = siteTitle
          
          let metaDescTag = document.querySelector('meta[name="description"]')
          if (!metaDescTag) {
            metaDescTag = document.createElement('meta')
            metaDescTag.setAttribute('name', 'description')
            document.head.appendChild(metaDescTag)
          }
          metaDescTag.setAttribute('content', siteDescription)
          
          // Update Open Graph tags
          const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta')
          if (!document.querySelector('meta[property="og:title"]')) {
            ogTitle.setAttribute('property', 'og:title')
            document.head.appendChild(ogTitle)
          }
          ogTitle.setAttribute('content', siteTitle)
          
          const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta')
          if (!document.querySelector('meta[property="og:description"]')) {
            ogDescription.setAttribute('property', 'og:description')
            document.head.appendChild(ogDescription)
          }
          ogDescription.setAttribute('content', siteDescription)
        }

        // Convert IDs to strings
        setTopNews(normalizeList(topData))
        const latestNormalized = normalizeList(latestData)
        const viralNormalized = normalizeList(viralData)
        setLatestNews(latestNormalized)
        // Important News uses viral articles (different from Latest News which uses /latest)
        setImportantNews(viralNormalized.slice(0, 10))
        setImportantPage(1)
        setHasMoreImportant(viralNormalized.length > 10)
        setViralNews(viralNormalized)
        setWebStories(normalizeList(webStoriesData))
        setCategories((Array.isArray(categoriesData) ? categoriesData : []).map((c: any) => ({ ...c, id: String(c.id) })))
      } catch (error) {
        console.error("Failed to fetch data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const loadMoreImportant = useCallback(async () => {
    if (loadingMoreImportant || !hasMoreImportant) return
    setLoadingMoreImportant(true)
    try {
      const nextPage = importantPage + 1
      const pageSize = 10
      // After the first "latest" page, use pre-fetched viral news as the next sets
      const start = (nextPage - 1) * pageSize
      const end = start + pageSize
      const nextList = viralNews.slice(start, end)

      if (nextList.length === 0) {
        setHasMoreImportant(false)
        return
      }
      setImportantNews((prev) => {
        const seen = new Set(prev.map((a) => a.id))
        const deduped = nextList.filter((a: Article) => !seen.has(a.id))
        return [...prev, ...deduped]
      })
      setImportantPage(nextPage)
      setHasMoreImportant(end < viralNews.length)
    } finally {
      setLoadingMoreImportant(false)
    }
  }, [hasMoreImportant, importantPage, loadingMoreImportant, viralNews])

  const loadMoreLatest = useCallback(async () => {
    if (loadingMoreLatest || !hasMoreLatest) return
    setLoadingMoreLatest(true)
    try {
      const nextPage = latestPage + 1
      const pageSize = 10
      const offset = (nextPage - 1) * pageSize
      const res = await fetch(`${API_BASE}/public/articles/latest?limit=${pageSize}&offset=${offset}`)
      if (!res.ok) {
        console.error("[RootHomePage] latest load more failed:", { url: res.url, status: res.status })
        setHasMoreLatest(false)
        return
      }
      const json = await safeJson(res)
      const nextList = normalizeList(json)
      if (nextList.length === 0) {
        setHasMoreLatest(false)
        return
      }
      setLatestNews((prev) => {
        const seen = new Set(prev.map((a) => a.id))
        const deduped = nextList.filter((a: Article) => !seen.has(a.id))
        if (deduped.length === 0) {
          setHasMoreLatest(false)
          return prev
        }
        return [...prev, ...deduped]
      })
      setLatestPage(nextPage)
    } finally {
      setLoadingMoreLatest(false)
    }
  }, [hasMoreLatest, latestPage, loadingMoreLatest])

  const filteredHomeFeed = useMemo(
    () =>
      latestNews.filter((a) => {
        const isTopNews = topNews.length > 0 && a.id === topNews[0].id
        return !isTopNews
      }),
    [latestNews, topNews],
  )

  const homeFeedLoadRef = useRef({
    visibleCount: HOME_FEED_INITIAL,
    filteredLen: 0,
    hasMoreLatest: true,
    loadingMoreLatest: false,
  })
  homeFeedLoadRef.current = {
    visibleCount: homeFeedVisibleCount,
    filteredLen: filteredHomeFeed.length,
    hasMoreLatest,
    loadingMoreLatest,
  }

  const homeFeedSentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = homeFeedSentinelRef.current
    if (!el || loading) return

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e.isIntersecting)
        if (!hit) return
        const s = homeFeedLoadRef.current
        if (s.loadingMoreLatest) return
        if (s.visibleCount < s.filteredLen) {
          setHomeFeedVisibleCount(homeFeedLoadRef.current.filteredLen)
          return
        }
        if (s.hasMoreLatest) {
          loadMoreLatest()
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [loadMoreLatest, loading, filteredHomeFeed.length])

  const wasLoadingLatestRef = useRef(false)
  useEffect(() => {
    if (wasLoadingLatestRef.current && !loadingMoreLatest) {
      setHomeFeedVisibleCount(filteredHomeFeed.length)
    }
    wasLoadingLatestRef.current = loadingMoreLatest
  }, [loadingMoreLatest, filteredHomeFeed.length])

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY
      const threshold = document.documentElement.scrollHeight - 200

      if (!loadingMoreImportant && hasMoreImportant && scrollPosition >= threshold) {
        loadMoreImportant()
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [loadMoreImportant, loadingMoreImportant, hasMoreImportant])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const displayedHomeFeed = filteredHomeFeed.slice(0, homeFeedVisibleCount)

  return (
    <HomePageLayout categories={categories}>
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Main Content */}
          <main className="col-span-12 lg:col-span-8">
            {/* Top News - Large Featured */}
            {topNews.length > 0 && (
              <div className="mb-8">
                <TopNewsCard article={topNews[0]} />
              </div>
            )}

            {/* Web Stories */}
            <div className="mb-8">
              <WebStoriesList articles={webStories} />
            </div>

            {/* Latest News */}
            <div className="mb-8">
              <div 
                className="flex items-center rounded-md mb-4"
                style={{
                  backgroundColor: "#4a4a4a",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  position: "relative"
                }}
              >
                <div 
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    backgroundColor: "#D00501",
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px"
                  }}
                />
                <span 
                  className="font-semibold"
                  style={{ 
                    fontSize: "14px", 
                    color: "#FFFFFF",
                    marginLeft: "8px"
                  }}
                >
                  Home Feed
                </span>
              </div>
              <LatestNewsList articles={displayedHomeFeed} />
              {(homeFeedVisibleCount < filteredHomeFeed.length || hasMoreLatest) && (
                <div ref={homeFeedSentinelRef} className="h-px w-full" aria-hidden />
              )}
              {loadingMoreLatest && (
                <p className="text-center text-sm text-muted-foreground mt-4">Loading more…</p>
              )}
            </div>
          </main>

          {/* Right Sidebar - Viral/Important News (sticky on large screens) */}
          <aside className="col-span-12 lg:col-span-4 lg:sticky lg:top-28 self-start h-fit">
            <ViralNewsSidebar
              importantArticles={importantNews}
              viralArticles={viralNews}
              onLoadMoreImportant={loadMoreImportant}
              hasMoreImportant={hasMoreImportant}
            />
          </aside>
        </div>
      </div>
    </HomePageLayout>
  )
}
